import { useState, useEffect, useCallback } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, SectionTitle, RefreshBtn } from "../primitives";

export const BackupStatus = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback((showLoading = true) => {
    if (showLoading) setLoading(true);
    apiCall("/settings/backups").then(d => { if (d) setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(true); }, [load]);
  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => load(false), 30000);
    return () => clearInterval(interval);
  }, [load]);

  const runBackup = async () => {
    setRunning(true); setMsg("");
    try {
      const res = await apiCall("/settings/backups/run", { method: "POST" });
      setMsg(res.success ? "Backup completed successfully" : "Backup failed");
      setTimeout(() => load(), 2000);
    } catch { setMsg("Backup failed"); }
    setRunning(false);
  };

  if (loading) return <div style={{ padding: 20, color: T.txM }}>Loading backup status...</div>;
  if (!data) return <div style={{ padding: 20, color: T.red }}>Failed to load backup status</div>;

  return (
    <div>
      <SectionTitle sub="Daily backup logs for database, files, GitHub, and Google Drive" right={
        <div style={{ display: "flex", gap: 8 }}>
          <RefreshBtn onClick={() => { load(); }} />
          <Btn v="primary" sz="sm" onClick={() => { runBackup(); }} disabled={running}>{running ? "Running..." : "Run Backup Now"}</Btn>
        </div>
      }>Backup Status</SectionTitle>

      {msg && <div style={{ padding: "8px 12px", backgroundColor: msg.includes("success") ? T.grnS : T.redS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: msg.includes("success") ? T.grn : T.red }}>{msg}</div>}

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 12 }}>Local Backups ({data.backups.length} snapshots)</div>
        <div style={{ maxHeight: 300, overflow: "auto" }}>
          {data.backups.map((b: any) => {
            const dateFormatted = b.date.replace(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/, "$2/$3/$1 $4:$5:$6");
            return (
              <div key={b.date} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${T.brd}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, minWidth: 140 }}>{dateFormatted}</div>
                <div style={{ display: "flex", gap: 8, flex: 1 }}>
                  {b.files.db && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, backgroundColor: T.grnS, color: T.grn }}>DB: {b.files.db.sizeHuman}</span>}
                  {b.files.uploads && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, backgroundColor: T.accS, color: T.acc }}>Files: {b.files.uploads.sizeHuman}</span>}
                  {b.files.env && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, backgroundColor: T.bg3, color: T.txM }}>Env: {b.files.env.sizeHuman}</span>}
                </div>
              </div>
            );
          })}
          {data.backups.length === 0 && <div style={{ fontSize: 12, color: T.txM, padding: 10 }}>No backups found</div>}
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>Off-Host Backup (Hetzner Storage Box, encrypted via restic)</span>
          <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, backgroundColor: data.resticStatus === "connected" ? T.grnS : T.redS, color: data.resticStatus === "connected" ? T.grn : T.red }}>{data.resticStatus === "connected" ? "Healthy" : (data.resticStatus || "Unknown")}</span>
        </div>
        {data.resticRepository ? (
          <div style={{ fontSize: 10, color: T.txD, marginBottom: 6 }}>Repository: <code>{data.resticRepository}</code></div>
        ) : null}
        {data.resticLastRun ? (
          <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>Last run: {data.resticLastRun}</div>
        ) : null}
        {data.resticSnapshots && data.resticSnapshots.length > 0 ? (
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 10, color: T.txD, borderBottom: `1px solid ${T.brd}`, fontWeight: 700 }}>
              <span>Snapshot ID</span><span>Time</span><span>Tag</span>
            </div>
            {data.resticSnapshots.map((s: any, i: number) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 11, color: T.txM, borderBottom: `1px solid ${T.brd}`, fontFamily: "monospace" }}>
                <span>{s.id}</span><span>{s.time}</span><span>{s.tag || "\u2014"}</span>
              </div>
            ))}
          </div>
        ) : <div style={{ fontSize: 12, color: T.txM }}>No snapshots found</div>}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 8 }}>GitHub Commits (Last 10)</div>
        <pre style={{ fontSize: 11, color: T.txM, fontFamily: "monospace", whiteSpace: "pre-wrap", lineHeight: 1.6, margin: 0, padding: 8, backgroundColor: T.bg3, borderRadius: 4 }}>{data.gitLog || "No commits found"}</pre>
      </Card>

      <Card>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 8 }}>Backup Log (Last 50 lines)</div>
        <pre style={{ fontSize: 10, color: T.txM, fontFamily: "monospace", whiteSpace: "pre-wrap", lineHeight: 1.5, margin: 0, padding: 8, backgroundColor: T.bg3, borderRadius: 4, maxHeight: 300, overflow: "auto" }}>{data.log || "No log found"}</pre>
      </Card>
    </div>
  );
};
